import nominalTranslation from './json/nominal-translation.json'
import SST_NAIL_ENUM from './json/SST_NAIL_ENUM.json'
import {
  ANSI_TPI,
  APP_STATE_KEY,
  BUILDING_CODE,
  CONNECTION_TYPE,
  DEFAULT_JOBSETTINGS_STATE,
  DEFAULT_NUMBER_OF_PLIES,
  DEFAULT_SETTING_STATE,
  HANGER_TYPE,
  LEDGER_MATERIAL_LIST,
  MEMBER_TYPE,
  OFFSET_TYPE,
} from './constants'
import { loadValue, saveValue } from './localStorage'
import { COUNTRY, GLOBAL_STATE_NAME } from '../../../utils/constants'
import isPlainObject from 'lodash/isPlainObject'

// returns a function which concatenate all the given classes
export const concatClasses = (...args) => {
  return args.reduce((acc, className) => (className ? `${acc} ${className}` : acc), '')
}

// returns a function which calls all the given functions
export const callAll =
  (...fns) =>
  (...args) =>
    fns.forEach((fn) => fn && fn(...args))

// Using API Staging as default at the moment
export const getApiBaseUrl = (value) => {
  switch (value) {
    case 'app.strongtie.com':
      return 'https://api.strongtie.com/gws'
    case 'app-staging.strongtie.com':
      return 'https://api-staging.strongtie.com/gws'
    case 'app-dev.mystrongtie.com':
      return 'https://api-staging.strongtie.com/gws'
    default:
      return 'https://api-staging.strongtie.com/gws'
  }
}

export const getHookFormName = (str = '') => str.substring(str.lastIndexOf('.') + 1, str.length)
export const isEmpty = (value) => value.length === 0
export const isUndefined = (value) => value === undefined
export const isNull = (value) => value === null
export const isOneDropdownOption = (options) => options.length <= 1
export const isArray = (value) => Array.isArray(value)
export const isIncludes = (key, ...args) => args.join(' ').toLowerCase().includes(key)
export const isNominalType = (type) => ['solidSawn', 'ledger'].includes(type)
export const isCarryingMember = (memberType) => ['header', 'girder'].includes(memberType)
export const isShowNailer = (memberType) =>
  ['nailer', 'wallSGap', 'wallSFlush', 'wallDGap', 'wallDFlush', 'wallDFlushStud'].includes(
    memberType,
  )
export const isCalculationAction = (actionType) => actionType.toLowerCase().endsWith('calculation')
export const isUploadAction = (actionType) => actionType.toLowerCase() === 'upload'

export const isCalculationField = (field) =>
  ['jobID', 'quantity', 'memberID', 'hlcFlush'].includes(field)

// TODO: Adding a flag for graphic action
export const isCalculationGraphic = (actionType) => {
  const actionAffix = actionType.split('/')[0] || ''
  return [
    'settings',
    'jobSettings',
    'hanger',
    'header',
    'girder',
    'joist',
    'truss',
    'jack',
    'leftHip',
    'rightHip',
  ].includes(actionAffix)
}

// parse a string with FeetInchesSixteenths format and return a decimal value
// e.g. parseFeetInchesSixteenths(020412) = 28.75,
//      parseFeetInchesSixteenths(020404) = 28.25
export const parseFeetInchesSixteenths = (source) => {
  if (source.includes('.') || source.length !== 6 || !Number.isInteger(parseInt(source))) {
    return source
  }

  const feet = Number(source.substring(0, 2))
  const inches = Number(source.substring(2, 4))
  const sixteenths = Number(source.substring(4))

  if (inches < 12 && sixteenths < 16) {
    return feet * 12 + inches + sixteenths / 16
  } else {
    return source
  }
}

// returns a value corresponding to the type of input (Note: Only works with DOMEventTarget)
export const formatEventValue = (eventTarget) => {
  const { value, checked, type } = eventTarget
  const updatedValue = {
    checkbox: checked,
    text: value,
    range: Number(value),
  }

  return isUndefined(updatedValue[type]) ? value : updatedValue[type]
}

export const toAbsoluteValue = (value) => {
  if (typeof value === 'number') return Math.abs(value)

  if (typeof value === 'string') {
    const parsedFloat = parseFloat(value)
    if (!isNaN(parsedFloat)) return toAbsoluteValue(parsedFloat)
  }

  if (isArray(value)) return value.map(toAbsoluteValue)

  if (isPlainObject(value)) {
    return Object.keys(value).reduce((output, key) => {
      output[key] = toAbsoluteValue(value[key])
      return output
    }, {})
  }
  return value
}

export const deleteUndefinedField = (obj) => {
  Object.keys(obj).forEach((key) => {
    if (obj[key] instanceof Object) {
      deleteUndefinedField(obj[key])
    } else if (typeof obj[key] === 'undefined') {
      delete obj[key]
    }
  })

  return obj
}

export const createQueryString = (queryData) => {
  const queryStringArray = Object.keys(queryData).reduce((acc, key) => {
    acc.push(`${key}=${queryData[key]}`)
    return acc
  }, [])

  let queryString = ''
  if (queryStringArray.length) {
    queryString = `${queryStringArray.join('&')}`
  }

  return queryString
}

export const getNestedObject = (nestedObj, pathArr) => {
  return pathArr.reduce(
    (obj, key) => (obj && obj[key] !== 'undefined' ? obj[key] : undefined),
    nestedObj,
  )
}

export const getUniqueBy = (dataset, filterBy = (item) => item, mapBy = (item) => item) => [
  ...new Set(dataset.filter(filterBy).map(mapBy)),
]

export const stringToOpts = (dataset) => dataset.map((item) => ({ text: item, value: item }))

export const getIJoistFlgThicknessLabel = (material) => {
  const result = {
    35: '1 1/8"',
    40: '1 1/8"',
    36: '1 1/4"',
    41: '1 1/4"',
    37: '1 5/16"',
    42: '1 5/16"',
    38: '1 3/8',
    43: '1 3/8',
    39: '1 1/2"',
    44: '1 1/2"',
    45: '1 1/2"',
    default: '0"',
  }

  return result[material] || result.default
}

export const getIJoistFlgThicknessValue = (material) => {
  const result = {
    35: 1.125,
    40: 1.125,
    36: 1.25,
    41: 1.25,
    37: 1.313,
    42: 1.313,
    38: 1.375,
    43: 1.375,
    39: 1.5,
    44: 1.5,
    45: 1.5,
    default: 0,
  }

  return result[material] || result.default
}

export const getIJoistFlgWidthLabel = (width) => {
  const result = {
    1.5: '1 1/2"',
    1.75: '1 3/4"',
    2: '2"',
    2.08: '2 1/16"',
    2.0625: '2 1/16"',
    2.25: '2 1/4"',
    2.3: '2 5/16"',
    2.3125: '2 5/16"',
    2.5: '2 1/2"',
    2.5625: '2 9/16"',
    3.5: '3 1/2"',
  }

  return result[width]
}

export const getIJoistFlgWidthValue = (width) => {
  const result = {
    2.08: 2.0625,
    2.3: 2.3125,
  }

  return result[width] || width
}

export const translateNominal = (width, depth, isRough) => {
  const item = nominalTranslation.find((i) => i.inputWidth === width && i.inputHeight === depth)

  return item
    ? isRough
      ? { width: item.roughWidth, depth: item.roughHeight }
      : { width: item.nominalWidth, depth: item.nominalHeight }
    : { width, depth }
}

export const getDefaultOptValue = (opts) => {
  const defaultOpt = opts.find((item) => item.default) || opts[0]
  return defaultOpt ? defaultOpt.value : ''
}

export const getFloorTrussValue = (width) =>
  typeof width === 'string'
    ? { width: 3.5, topChord: width === 'single' ? 1 : 2 }
    : { width, topChord: 1 }

export const toPercentage = (number) => `${parseFloat(number).toFixed(2)}%`

export const modifyAction = (state, action) => {
  const { name, value } = action.payload

  state[name] = value
}

export const updateAction = (state, action) => {
  const updatedState = { ...state, ...action.payload }

  return updatedState
}

export const saveAppState = (state) => saveValue(APP_STATE_KEY, state)

export const loadAppState = () => loadValue(APP_STATE_KEY)

export const getAppState = (country, connectionType) => {
  const state = {
    settings: {
      country,
    },
    jobSettings: {
      ansitpi: ANSI_TPI[country],
      upliftLoadDurationType: country === COUNTRY.USA ? 160 : 115,
      downloadDurationType:
        country === COUNTRY.USA ? (connectionType === CONNECTION_TYPE.JOIST ? 100 : 125) : 100,
    },
  }

  return state
}

export const populateAppState = (state) => {
  const { input } = state.calculation
  const { settings } = input

  const appState = getAppState(settings.country, settings.connectionType)

  const updatedSettings = Object.assign({}, DEFAULT_SETTING_STATE, appState.settings)
  const updatedJobSettings = Object.assign({}, DEFAULT_JOBSETTINGS_STATE, appState.jobSettings)

  const updatedAppState = Object.assign(
    {},
    {
      [GLOBAL_STATE_NAME.SETTINGS]: state[GLOBAL_STATE_NAME.SETTINGS]
        ? state[GLOBAL_STATE_NAME.SETTINGS]
        : { orientation: 'horizontal', fontSize: '-densityStandard' },
    },
    {
      calculation: {
        input: { settings: updatedSettings, jobSettings: updatedJobSettings },
      },
    },
  )

  return updatedAppState
}

export const RenderIf = (isTrue, children) => {
  return isTrue ? children : null
}

export const getLatestJobId = (currentJobsLength) => `Job ${currentJobsLength + 1}`

export const getInputLabel = (inputName) => {
  const parts = inputName.split('.')

  return parts[parts.length - 1]
}

// export const numberWithCommas = (x) => {
// return x
//   .toString()
//   .replace(/,/g, '')
//   .replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',')
// }

export const getGAValue = (list, value) => {
  const matchedOption = list.find((o) => o.value === value)
  return matchedOption ? matchedOption.text : 'N/A'
}

export const getModelImage = async () => {
  let img = ''
  const hsModel = document.querySelector('bp-hs-legacy')
  if (hsModel) {
    img = await hsModel.handleCaptureMesh(hsModel.handleFitCameraView)
  }
  return img
}

/**
 * This function is parsing the Formik field name to Reducer field name
 * @param {String}    field Formik field name
 * @returns {String}  Reducer field name
 */
export const parseFieldName = (field = '') => {
  const parts = field.split('.')

  return parts[parts.length - 1]
}

export const getSSTNail = (nailEnum) => {
  const nail = SST_NAIL_ENUM.find((item) => item.enum === nailEnum)

  return nail
}

export const prepareJobData = (jobInputs, selectedRow) => {
  const { jobID, quantity } = jobInputs.jobSettings
  const { tfNailQty, tfNailType, hNailType, hNailQty, jNailQty, jNailType } = selectedRow

  const tfNail = tfNailQty ? getSSTNail(tfNailType) : null
  const hNail = hNailQty[0] ? getSSTNail(hNailType[0]) : null
  const jNail = jNailQty ? getSSTNail(jNailType) : null

  const tfNailData = tfNail
    ? {
        type: tfNail.model,
        quantity: tfNailQty * quantity,
        sellableSkus: tfNail.sellableSkus,
      }
    : null

  const hNailData = hNail
    ? {
        type: hNail.model,
        quantity: hNailQty[0] * quantity,
        sellableSkus: hNail.sellableSkus,
      }
    : null

  const jNailData = jNail
    ? {
        type: jNail.model,
        quantity: jNailQty * quantity,
        sellableSkus: jNail.sellableSkus,
      }
    : null

  const jobData = {
    ...selectedRow,
    uid: jobID.trim(),
    quantity,
    jobInputs,
    tfNail: tfNailData,
    hNail: hNailData,
    jNail: jNailData,
  }

  return jobData
}

export const shouldDisplayLumberFinish = (memberType, material) =>
  !(isMemberTypeLedger(memberType) && Object.values(LEDGER_MATERIAL_LIST).includes(material))

export const getText = (list, value, fallbackValue = 'Not Applicable') => {
  const target = list?.find((item) => item?.value === value)
  return target?.text ?? fallbackValue
}

export const isInvalidOffsetDirection = (hangerType, topFlangeOffset) =>
  hangerType === HANGER_TYPE.ALL_TYPES && topFlangeOffset !== OFFSET_TYPE.CENTERED

export const isMemberTypeGlulam = (memberType) => memberType === MEMBER_TYPE.GLULAM
export const isMemberTypeSolidSawn = (memberType) => memberType === MEMBER_TYPE.SOLID_SAWN
export const isMemberTypeLedger = (memberType) => memberType === MEMBER_TYPE.LEDGER
export const isMemberTypeIJoist = (memberType) => memberType === MEMBER_TYPE.IJOIST
export const isHangerTypeConcealed = (hangerType) => hangerType === HANGER_TYPE.CONCEALED
export const isJoistConnectionType = (connectionType) => connectionType === CONNECTION_TYPE.JOIST
export const isMultiTrussConnectionType = (connectionType) =>
  connectionType === CONNECTION_TYPE.MULTI_TRUSS

export const shouldDisplayNumberOfPlies = (memberType, pliesOptions, isRough) =>
  !(
    (isMemberTypeGlulam(memberType) && isOneDropdownOption(pliesOptions)) ||
    (isMemberTypeSolidSawn(memberType) && isRough)
  )

export const getDefaultPlyValue = (pliesOptions) =>
  isEmpty(pliesOptions) ? DEFAULT_NUMBER_OF_PLIES : getDefaultOptValue(pliesOptions)

export const getICILabel = (ici) => {
  if (ici === 999999) return '-'
  else if (ici === 100) return 'Lowest'
  return `+${toPercentage(ici - 100)}`
}

export const escapeCSVValue = (text) => {
  return text.replace(/"/g, '""')
}
